import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import {
  SlideObject,
  TableData,
  resolveSlideFontFamily,
  resolvedFontWeight,
  resolvedLineHeight,
} from '../types';
import { sanitizePresentationHtml } from './textSanitize';
import {
  TABLE_BODY_BG,
  TABLE_BODY_COLOR,
  TABLE_FONT_SIZE,
  TABLE_HEADER_BG,
  TABLE_HEADER_COLOR,
  TABLE_ZEBRA,
  normalizeTable,
  renderedStrokeWidth,
  resizeTableColumns,
  resizeTableRows,
  tableChrome,
} from './tableModel';
import { usePresentationSettings } from '../PresentationSettingsContext';
import { hexForSettingsColor } from '../presentationSettings';

interface TableObjectViewProps {
  object: SlideObject;
  editingCellId?: string | null;
  onEditCell?: (cellId: string | null) => void;
  onCommitCell?: (cellId: string, html: string) => void;
  interactive?: boolean;
  canResizeLayout?: boolean;
  layoutHitPx?: number;
  onLayoutBegin?: () => void;
  onTableCommit?: (table: TableData) => void;
}

const TableCellEditor: React.FC<{
  html: string;
  color: string;
  onCommit: (html: string) => void;
  onTab: (shift: boolean) => void;
}> = ({ html, color, onCommit, onTab }) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.innerHTML = sanitizePresentationHtml(html) || '<p></p>';
    node.focus();
    const selection = window.getSelection();
    const range = document.createRange();
    range.selectNodeContents(node);
    range.collapse(false);
    selection?.removeAllRanges();
    selection?.addRange(range);
  }, [html]);

  return (
    <Box
      ref={ref}
      contentEditable
      suppressContentEditableWarning
      data-table-cell-editor="true"
      onPointerDown={(event) => {
        if (event.shiftKey || event.ctrlKey || event.metaKey) return;
        event.stopPropagation();
      }}
      onClick={(event) => {
        if (event.shiftKey || event.ctrlKey || event.metaKey) return;
        event.stopPropagation();
      }}
      onBlur={() => onCommitRef.current(ref.current?.innerHTML || '')}
      onKeyDown={(event) => {
        if (event.key === 'Tab') {
          event.preventDefault();
          event.stopPropagation();
          onCommitRef.current(ref.current?.innerHTML || '');
          onTab(event.shiftKey);
        }
      }}
      sx={{
        width: '100%',
        minHeight: '100%',
        outline: 'none',
        color,
        '& p': { m: 0, p: 0 },
      }}
    />
  );
};

const TableObjectView: React.FC<TableObjectViewProps> = ({
  object,
  editingCellId,
  onEditCell,
  onCommitCell,
  interactive,
  canResizeLayout,
  layoutHitPx = 8,
  onLayoutBegin,
  onTableCommit,
}) => {
  const { settings } = usePresentationSettings();
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const draftRef = useRef<TableData | null>(null);
  const [draftTable, setDraftTable] = useState<TableData | null>(null);
  const table = draftTable || normalizeTable(object.table);
  const headerBg = object.style?.backgroundColor || TABLE_HEADER_BG;
  const zebra = hexForSettingsColor(settings, settings.tableDefaults.zebraColorId, TABLE_ZEBRA);
  const headerColor = object.style?.color || TABLE_HEADER_COLOR;
  const grid = object.style?.strokeColor || '#998A6B';
  const fontSize = object.style?.fontSize || TABLE_FONT_SIZE;
  const chrome = tableChrome(object.style);
  const strokeWidth = renderedStrokeWidth(object.style);
  const align = object.style?.textAlign || 'left';
  const verticalAlign = object.style?.verticalAlign || 'middle';
  const bodyWeight = resolvedFontWeight(object.style);
  const lineHeight = resolvedLineHeight(object.style);
  const letterSpacing = object.style?.letterSpacing;
  const textTransform = object.style?.textTransform || 'none';
  const cells = table.rows.flatMap((row) => row.cells);
  const rowHeights = table.rowHeights?.length === table.rows.length
    ? table.rowHeights
    : table.rows.map(() => 1 / table.rows.length);
  const showDividers = Boolean(canResizeLayout);

  const editNeighbor = (cellId: string, shift: boolean) => {
    const index = cells.findIndex((cell) => cell.id === cellId);
    if (index < 0) return;
    const next = cells[shift ? Math.max(0, index - 1) : Math.min(cells.length - 1, index + 1)];
    onEditCell?.(next?.id || null);
  };

  const beginDivider = (
    event: React.PointerEvent,
    axis: 'col' | 'row',
    index: number,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const startTable = normalizeTable(draftRef.current || object.table);
    const startPos = axis === 'col' ? event.clientX : event.clientY;
    const size = axis === 'col' ? rect.width : rect.height;
    draftRef.current = startTable;
    setDraftTable(startTable);
    let began = false;
    const onMove = (moveEvent: PointerEvent) => {
      const delta = ((axis === 'col' ? moveEvent.clientX : moveEvent.clientY) - startPos) / size;
      if (!delta) return;
      if (!began) {
        onLayoutBegin?.();
        began = true;
      }
      const next = axis === 'col'
        ? resizeTableColumns(startTable, index, delta)
        : resizeTableRows(startTable, index, delta);
      draftRef.current = next;
      setDraftTable(next);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      const next = draftRef.current || startTable;
      draftRef.current = null;
      setDraftTable(null);
      if (began) onTableCommit?.(next);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  };

  return (
    <Box ref={wrapRef} sx={{ position: 'relative', width: '100%', height: '100%' }}>
      <Box
        component="table"
        sx={{
          width: '100%',
          height: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontFamily: resolveSlideFontFamily(object.style?.fontFamily),
          fontSize,
          lineHeight,
          letterSpacing: letterSpacing != null ? `${letterSpacing}px` : undefined,
          textTransform,
          '& p, & ul, & ol': { m: 0, p: 0 },
          '& ul, & ol': { pl: '1.2em' },
        }}
      >
        <colgroup>
          {table.colWidths.map((width, index) => (
            <col key={`${object.id}-col-${index}`} style={{ width: `${width * 100}%` }} />
          ))}
        </colgroup>
        <tbody>
          {table.rows.map((row, rowIndex) => {
            const isHeader = table.headerRow && rowIndex === 0;
            const bg = isHeader ? headerBg : rowIndex % 2 === 1 ? zebra : TABLE_BODY_BG;
            const color = isHeader ? headerColor : TABLE_BODY_COLOR;
            return (
              <Box
                component="tr"
                key={`${object.id}-row-${rowIndex}`}
                sx={{ height: `${(rowHeights[rowIndex] || (1 / table.rows.length)) * 100}%` }}
              >
                {row.cells.map((cell) => {
                  const editing = interactive && editingCellId === cell.id;
                  return (
                    <Box
                      component={isHeader ? 'th' : 'td'}
                      key={cell.id}
                      data-table-cell={cell.id}
                      onClick={interactive && !editing ? (event) => {
                        if (event.shiftKey || event.ctrlKey || event.metaKey) return;
                        onEditCell?.(cell.id);
                      } : undefined}
                      sx={{
                        border: `${strokeWidth}px solid ${grid}`,
                        p: `${chrome.padding}px`,
                        verticalAlign,
                        textAlign: align,
                        backgroundColor: bg,
                        color,
                        fontWeight: isHeader ? 700 : bodyWeight,
                        overflow: 'hidden',
                        cursor: 'inherit',
                      }}
                    >
                      {editing ? (
                        <TableCellEditor
                          html={cell.content}
                          color={color}
                          onCommit={(html) => onCommitCell?.(cell.id, html)}
                          onTab={(shift) => editNeighbor(cell.id, shift)}
                        />
                      ) : (
                        <Box
                          sx={{ pointerEvents: 'none' }}
                          dangerouslySetInnerHTML={{ __html: sanitizePresentationHtml(cell.content) }}
                        />
                      )}
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </tbody>
      </Box>
      {showDividers && table.colWidths.slice(0, -1).map((_, index) => {
        const left = table.colWidths.slice(0, index + 1).reduce((sum, value) => sum + value, 0) * 100;
        return (
          <Box
            key={`${object.id}-col-div-${index}`}
            data-table-divider="col"
            onPointerDown={(event) => beginDivider(event, 'col', index)}
            sx={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${left}%`,
              width: layoutHitPx,
              ml: `${-layoutHitPx / 2}px`,
              cursor: 'col-resize',
              zIndex: 3,
            }}
          />
        );
      })}
      {showDividers && rowHeights.slice(0, -1).map((_, index) => {
        const top = rowHeights.slice(0, index + 1).reduce((sum, value) => sum + value, 0) * 100;
        return (
          <Box
            key={`${object.id}-row-div-${index}`}
            data-table-divider="row"
            onPointerDown={(event) => beginDivider(event, 'row', index)}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: `${top}%`,
              height: layoutHitPx,
              mt: `${-layoutHitPx / 2}px`,
              cursor: 'row-resize',
              zIndex: 2,
            }}
          />
        );
      })}
    </Box>
  );
};

export default TableObjectView;
